import AppContext from 'App/AppContext';
import { IPIconButton, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { deleteIcon, globalStackTokensGapExtraSmall, upIcon } from 'globalStyles';
import Asset from 'models/asset/asset';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Classification from 'models/classification/classification';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { ClassificationTagItem } from './ClassificationTagItem';

interface IClassificationListItemProps {
  classifications: Classification[];
  compact?: boolean;
  onRemove?: (item: Classification) => void;
  onClick?: (item: Classification) => void;
  onAddInheritance?: (classification: Classification, assetId?: number, containerId?: number) => void;
  onRemoveInheritance?: (classification: Classification, assetId?: number) => void;
  asset?: Asset;
  //keyed by assetId; the caller builds both maps once per column rebuild
  viewAssetsById?: Map<number, Asset>;
  allAssetsById?: Map<number, Asset>;
  isUpdating?: boolean;
  maxWidth?: number;
}

export const ClassificationTagList = (props: IClassificationListItemProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['classifications']);
  const sortedClassifications = [...props.classifications];
  const [canUpdate] = useState<boolean>(
    hasUserDataPermission(appContext, [props.asset?.authSchemaId], AuthSchemaLineOperation.Update),
  );

  sortedClassifications.sort((a, b) => Classification.sortOnGroup(a, b));
  const parentAssetId =
    props.asset && props.asset.containers && props.asset.containers.length > 0
      ? props.asset.containers[0].assetId
      : undefined;
  const parentAsset = parentAssetId ? props.viewAssetsById?.get(parentAssetId) : undefined;
  //the display set is a filtered clone whose containers lost their classification groups, so the
  //asset and its first container come from the full set, resolved once per render and not per tag
  const sourceAsset = props.asset ? props.allAssetsById?.get(props.asset.assetId) : undefined;
  const sourceParent = parentAssetId ? props.allAssetsById?.get(parentAssetId) : undefined;
  const parentContainer = sourceAsset?.containers?.find((c) => c.assetId === parentAssetId);

  const parentContainerLacksGroup = (classification: Classification): boolean => {
    if (!parentContainer) return false;

    return (
      parentContainer.classificationGroups === undefined ||
      !parentContainer.classificationGroups.includes(classification.group.stringValueId)
    );
  };

  const canPropagateClassification = (classification: Classification): boolean => {
    if (props.isUpdating === true) return false;
    if (!sourceAsset || !parentAssetId || !parentAsset) return false;

    return parentContainerLacksGroup(classification);
  };

  const canRemoveClassification = (classification: Classification): boolean => {
    if (props.isUpdating === true) return false;
    if (!sourceAsset) return false;
    if (!sourceParent) return true;

    return parentContainerLacksGroup(classification);
  };

  const getTooltipContentAdd = (classification: Classification): string => {
    if (!sourceAsset?.containers?.[0]) return t('classifications:Inheritance.NoContainer');
    if (!parentAsset) return t('classifications:Inheritance.NoContainerWithCurrentFilters');
    if (!canPropagateClassification(classification)) return t('classifications:Inheritance.AlreadyPropagated');

    return t('classifications:Inheritance.Propagate');
  };

  const getTooltipContentRemove = (classification: Classification): string => {
    if (!canRemoveClassification(classification)) return t('classifications:Inheritance.CannotRemove');

    return t('classifications:Inheritance.Remove');
  };

  //
  // Main render
  //
  return (
    <IPStack horizontal wrap>
      {sortedClassifications?.map((c) => {
        return (
          <IPStack.Item key={c.classificationId}>
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
              <ClassificationTagItem
                classification={c}
                onRemove={props.onRemove}
                onClick={props.onClick}
                compact={props.compact}
                maxWidth={props.maxWidth}
              />
              {props.onAddInheritance && canUpdate && (
                <IPTooltipHost content={getTooltipContentAdd(c)}>
                  <IPIconButton
                    disabled={!canPropagateClassification(c)}
                    iconProps={upIcon}
                    onClick={() => props.onAddInheritance!(c, props.asset?.assetId, parentAsset?.assetId)}
                  />
                </IPTooltipHost>
              )}
              {props.onRemoveInheritance && canUpdate && c.inherited && (
                <IPTooltipHost content={getTooltipContentRemove(c)}>
                  <IPIconButton
                    disabled={!canRemoveClassification(c)}
                    iconProps={deleteIcon}
                    onClick={() => props.onRemoveInheritance!(c, props.asset?.assetId)}
                  />
                </IPTooltipHost>
              )}
            </IPStack>
          </IPStack.Item>
        );
      })}
    </IPStack>
  );
};
